import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { GoogleOneTapProvider } from './components/GoogleOneTapManager';
import { onAuthStateChanged } from "firebase/auth";
import './setupFetchInterceptors';
import { auth, db, environment } from './config/firebase.config';
import { diagnoseAuthDelay } from './utils/authUtils';
import { ensureAppRuntime } from './app/runtime';
import { ensureDiscoveryRuntime } from './app/discoveryRuntime';
import { loginMachine } from './auth/loginMachine';

ensureAppRuntime();
ensureDiscoveryRuntime();

function hasEmailSignInActionLink() {
  const hasActionParams = (rawParams) => {
    const params = new URLSearchParams(rawParams);
    return params.get('mode') === 'signIn' && params.has('oobCode') && params.has('apiKey');
  };

  if (hasActionParams(window.location.search)) return true;

  const hash = window.location.hash || '';
  const hashQuery = hash.includes('?') ? hash.slice(hash.indexOf('?')) : hash.replace(/^#/, '');
  return hasActionParams(hashQuery);
}

// Check if we should render immediately or wait for landing page
const rootElement = document.getElementById('root');

if (!rootElement) {
  console.error('[React] Root element not found. Make sure index.html has a div with id="root"');
} else {
  // Create root but only render when landing page is ready
  const root = ReactDOM.createRoot(rootElement);

  // Function to render the app
  const renderApp = () => {
    root.render(
      <React.StrictMode>
        <GoogleOneTapProvider>
          <App />
        </GoogleOneTapProvider>
      </React.StrictMode>
    );
    window.REACT_APP_LOADED = true;
  };

  const renderAppAfterAuthLinkHandling = () => {
    if (!hasEmailSignInActionLink()) {
      renderApp();
      return;
    }

    loginMachine.start()
      .catch((error) => {
        console.error('[Firebase Auth] Email link startup failed before app render:', error);
      })
      .finally(renderApp);
  };

  // Check if we should skip the landing page (for direct app access)
  const params = new URLSearchParams(window.location.search);
  const skipLanding = params.get('direct') === 'true' || window.location.hash === '#map' || hasEmailSignInActionLink();

  if (skipLanding || rootElement.style.display !== 'none') {
    // Direct access to app, skip landing page
    renderAppAfterAuthLinkHandling();
  } else {
    // Wait for landing page to signal readiness
    const checkReady = setInterval(() => {
      if (window.REACT_APP_READY) {
        clearInterval(checkReady);
        renderAppAfterAuthLinkHandling();
      }
    }, 100);

    // Timeout after 10 seconds to prevent hanging
    setTimeout(() => {
      clearInterval(checkReady);
      if (!window.REACT_APP_LOADED && !window.REACT_APP_READY) {
        console.error('[React] Timeout waiting for landing page signal');
        renderAppAfterAuthLinkHandling(); // Try to render anyway
      }
    }, 10000);
  }
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// Hide the static splash screen once React is ready (unless ?testsplashscreen=true)
const splashEl = document.getElementById('splash-screen');
if (splashEl) {
  const params = new URLSearchParams(window.location.search);
  const keepSplash = params.get('testsplashscreen') === 'true';
  if (!keepSplash) {
    setTimeout(() => {
      splashEl.classList.add('hidden');
      // Fully remove from layout after the fade-out transition
      splashEl.addEventListener('transitionend', () => splashEl.remove(), { once: true });
    }, 1000); // 1 second delay
  }
}

console.log(`[Firebase] Initializing with ${environment} environment`);
console.log(`[Firebase] Project ID: ${auth.app.options.projectId}`);

// Log auth initialization for debugging
console.log('[Firebase Auth] Active project:', auth.app.options.projectId, 'Environment:', environment);

// Determine if running locally (used only for logging now)
const isLocalDevelopment = window.location.hostname === 'localhost' ||
                          window.location.hostname === '127.0.0.1' ||
                          window.location.hostname.startsWith('192.168.') ||
                          window.location.hostname.startsWith('10.') ||
                          (window.location.hostname.startsWith('172.') &&
                           parseInt(window.location.hostname.split('.')[1]) >= 16 &&
                           parseInt(window.location.hostname.split('.')[1]) <= 31);

// Diagnose potential auth delays in development
if (environment === 'development' && isLocalDevelopment) {
  setTimeout(() => {
    diagnoseAuthDelay('development-startup');
  }, 1000);
}

// Call once at startup
export async function ensureSignedIn() {
  console.log('[ensureSignedIn] Delegating startup auth to loginMachine');
  return loginMachine.start();
}

// Hook you can use in components if you need user info
export function subscribeToAuth(cb) {
  console.log('[subscribeToAuth] Setting up auth subscription');
  return onAuthStateChanged(auth, (user) => {
    console.log('[subscribeToAuth] Auth state change callback:', {
      uid: user?.uid || 'null',
      isAnonymous: user?.isAnonymous,
      email: user?.email || 'null'
    });
    cb(user);
  });
}

// Initialize auth after DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  ensureSignedIn().then((user) => {
    console.log('[ensureSignedIn] Initialization complete, final user:', user?.uid || 'null');
  });
});

// Re-export core Firebase instances for other modules (legacy compatibility)
export { auth, db, environment };

// Ensure Android status bar matches DetailsCard slate color
(() => {
  const setThemeColour = (clr) => {
    let meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'theme-color';
      document.head.appendChild(meta);
    }
    meta.content = clr;
  };
  setThemeColour('#1f2937');
})();
